import React from 'react';
import {
  Box,
  Flex,
  IconButton,
  Image,
  Text,
  VStack,
  HStack,
  Badge,
  Skeleton,
} from '@chakra-ui/react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { MdFavorite, MdFavoriteBorder } from 'react-icons/md';
import type { LiveChannel } from '../types';
import { streamGhostOnDarkHover, streamGhostPinkHover } from '../darkThemeButtonProps';

interface Props {
  channels: LiveChannel[];
  viewMode: 'grid' | 'list';
  isFavorite: (id: string) => boolean;
  onToggleFavorite: (ch: LiveChannel) => void;
  onPlay: (ch: LiveChannel) => void;
  loading?: boolean;
  /** Dense rows for Live TV desktop layout */
  compact?: boolean;
  /** Scroll container ref (required for virtualized compact list) */
  scrollElementRef?: React.RefObject<HTMLElement | null>;
}

/** Virtual row height — keep in sync with compact row (logo + padding) */
const COMPACT_ROW_ESTIMATE = 52;

function Logo({ src, name, compact }: { src?: string; name: string; compact?: boolean }) {
  const box = compact ? '34px' : '44px';
  if (src) {
    return (
      <Image
        src={src}
        alt=""
        boxSize={box}
        borderRadius={compact ? 'md' : 'md'}
        objectFit="cover"
        bg="whiteAlpha.100"
        fallback={<Skeleton boxSize={box} borderRadius={compact ? 'md' : 'md'} />}
      />
    );
  }
  return (
    <Flex
      boxSize={box}
      borderRadius={compact ? 'md' : 'md'}
      bg="whiteAlpha.100"
      align="center"
      justify="center"
      fontSize={compact ? '11px' : '10px'}
      fontWeight="700"
      color="teal.200"
      letterSpacing="-0.02em"
      flexShrink={0}
    >
      {name.slice(0, 2).toUpperCase()}
    </Flex>
  );
}

const ChannelRowCompact = React.memo(function ChannelRowCompact({
  ch,
  fav,
  onPlay,
  onToggleFavorite,
}: {
  ch: LiveChannel;
  fav: boolean;
  onPlay: (ch: LiveChannel) => void;
  onToggleFavorite: (ch: LiveChannel) => void;
}) {
  return (
    <Flex
      align="center"
      gap={2}
      py={0.5}
      px={1.5}
      borderRadius="sm"
      bg="whiteAlpha.50"
      borderWidth="1px"
      borderColor="whiteAlpha.100"
      cursor="pointer"
      _hover={{ borderColor: 'teal.400' }}
      onClick={() => onPlay(ch)}
      minH={`${COMPACT_ROW_ESTIMATE - 4}px`}
    >
      <Logo src={ch.logo} name={ch.name} compact />
      <Box
        flex={1}
        minW={0}
        lineHeight="1.1"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        minH="34px"
      >
        <HStack spacing={0.5} align="center">
          <Text
            fontWeight="600"
            color="white"
            noOfLines={1}
            fontSize="11px"
            letterSpacing="-0.01em"
          >
            {ch.name}
          </Text>
          <Badge
            colorScheme="red"
            variant="subtle"
            fontSize="7px"
            px={0.5}
            py={0}
            lineHeight="1.1"
          >
            LIVE
          </Badge>
        </HStack>
        {ch.epgCurrent && (
          <Text fontSize="9px" color="whiteAlpha.500" noOfLines={1}>
            {ch.epgCurrent}
          </Text>
        )}
      </Box>
      <IconButton
        aria-label="Favorite"
        icon={fav ? <MdFavorite /> : <MdFavoriteBorder />}
        size="xs"
        minW="30px"
        h="30px"
        variant="ghost"
        color={fav ? 'pink.300' : 'whiteAlpha.500'}
        {...(fav ? streamGhostPinkHover : streamGhostOnDarkHover)}
        sx={{
          '& svg': { width: '16px', height: '16px' },
        }}
        onClick={(e) => {
          e.stopPropagation();
          onToggleFavorite(ch);
        }}
      />
    </Flex>
  );
});

export function ChannelList({
  channels,
  viewMode,
  isFavorite,
  onToggleFavorite,
  onPlay,
  loading,
  compact,
  scrollElementRef,
}: Props) {
  const virtualize = Boolean(compact && scrollElementRef);

  const virtualizer = useVirtualizer({
    count: virtualize ? channels.length : 0,
    getScrollElement: () => scrollElementRef?.current ?? null,
    estimateSize: () => COMPACT_ROW_ESTIMATE,
    overscan: 16,
  });

  const virtualItems = virtualize ? virtualizer.getVirtualItems() : [];

  if (loading) {
    return (
      <VStack align="stretch" spacing={compact ? 0 : 2}>
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} height={compact ? '28px' : '64px'} borderRadius={compact ? 'sm' : 'lg'} />
        ))}
      </VStack>
    );
  }

  if (!channels.length) {
    return (
      <Text fontSize="sm" color="whiteAlpha.500">
        No channels in this category.
      </Text>
    );
  }

  if (viewMode === 'grid') {
    return (
      <Box
        display="grid"
        gridTemplateColumns={{ base: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)', lg: 'repeat(5, 1fr)' }}
        gap={3}
      >
        {channels.map((ch) => (
          <Box
            key={ch.id}
            role="group"
            borderRadius="xl"
            bg="whiteAlpha.50"
            borderWidth="1px"
            borderColor="whiteAlpha.100"
            p={3}
            cursor="pointer"
            transition="all 0.15s"
            _hover={{ borderColor: 'teal.400', transform: 'translateY(-2px)' }}
            onClick={() => onPlay(ch)}
          >
            <VStack spacing={2} align="stretch">
              <HStack justify="space-between" align="flex-start">
                <Logo src={ch.logo} name={ch.name} compact={compact} />
                <IconButton
                  aria-label="Favorite"
                  icon={isFavorite(ch.id) ? <MdFavorite /> : <MdFavoriteBorder />}
                  size="sm"
                  variant="ghost"
                  color={isFavorite(ch.id) ? 'pink.300' : 'whiteAlpha.500'}
                  {...(isFavorite(ch.id) ? streamGhostPinkHover : streamGhostOnDarkHover)}
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleFavorite(ch);
                  }}
                />
              </HStack>
              <Text fontSize="sm" fontWeight="600" color="white" noOfLines={2}>
                {ch.name}
              </Text>
              {ch.epgCurrent && (
                <Text fontSize="xs" color="whiteAlpha.500" noOfLines={2}>
                  {ch.epgCurrent}
                </Text>
              )}
              <Badge alignSelf="flex-start" colorScheme="red" variant="subtle" fontSize="10px">
                LIVE
              </Badge>
            </VStack>
          </Box>
        ))}
      </Box>
    );
  }

  if (virtualize) {
    return (
      <Box position="relative" w="100%" h={`${virtualizer.getTotalSize()}px`}>
        {virtualItems.map((vi) => {
          const ch = channels[vi.index];
          return (
            <Box
              key={ch.id}
              position="absolute"
              top={0}
              left={0}
              w="100%"
              transform={`translateY(${vi.start}px)`}
              pb="2px"
            >
              <ChannelRowCompact
                ch={ch}
                fav={isFavorite(ch.id)}
                onPlay={onPlay}
                onToggleFavorite={onToggleFavorite}
              />
            </Box>
          );
        })}
      </Box>
    );
  }

  return (
    <VStack align="stretch" spacing={compact ? 0 : 2}>
      {channels.map((ch) => (
        <Flex
          key={ch.id}
          align="center"
          gap={compact ? 2 : 3}
          py={compact ? 0.5 : 3}
          px={compact ? 1.5 : 3}
          borderRadius={compact ? 'sm' : 'lg'}
          bg="whiteAlpha.50"
          borderWidth="1px"
          borderColor="whiteAlpha.100"
          cursor="pointer"
          _hover={{ borderColor: 'teal.400' }}
          onClick={() => onPlay(ch)}
        >
          <Logo src={ch.logo} name={ch.name} compact={compact} />
          <Box
            flex={1}
            minW={0}
            lineHeight={compact ? '1.1' : undefined}
            display="flex"
            flexDirection="column"
            justifyContent="center"
            minH={compact ? '34px' : '44px'}
          >
            <HStack spacing={compact ? 0.5 : 1} align="center">
              <Text
                fontWeight="600"
                color="white"
                noOfLines={1}
                fontSize={compact ? '11px' : 'md'}
                letterSpacing={compact ? '-0.01em' : undefined}
              >
                {ch.name}
              </Text>
              <Badge
                colorScheme="red"
                variant="subtle"
                fontSize={compact ? '7px' : '9px'}
                px={compact ? 0.5 : 1}
                py={0}
                lineHeight={compact ? '1.1' : undefined}
              >
                LIVE
              </Badge>
            </HStack>
            {ch.epgCurrent && (
              <Text
                fontSize={compact ? '9px' : 'sm'}
                color="whiteAlpha.500"
                noOfLines={1}
                mt={compact ? 0 : undefined}
              >
                {compact ? ch.epgCurrent : `Now: ${ch.epgCurrent}${ch.epgNext ? ` · Next: ${ch.epgNext}` : ''}`}
              </Text>
            )}
          </Box>
          <IconButton
            aria-label="Favorite"
            icon={isFavorite(ch.id) ? <MdFavorite /> : <MdFavoriteBorder />}
            size={compact ? 'xs' : 'sm'}
            minW={compact ? '30px' : undefined}
            h={compact ? '30px' : undefined}
            variant="ghost"
            color={isFavorite(ch.id) ? 'pink.300' : 'whiteAlpha.500'}
            {...(isFavorite(ch.id) ? streamGhostPinkHover : streamGhostOnDarkHover)}
            sx={
              compact
                ? {
                    '& svg': { width: '16px', height: '16px' },
                  }
                : undefined
            }
            onClick={(e) => {
              e.stopPropagation();
              onToggleFavorite(ch);
            }}
          />
        </Flex>
      ))}
    </VStack>
  );
}
